// @flow
import React, { useRef, useState } from 'react';
import { TextField, Button, unmaskValue } from '@cmsgov/ds-healthcare-gov';
import { Dropdown } from '@cmsgov/ds-healthcare-gov-10';
import { runValidations } from '../../../utilities/validation';
import { validators } from '../../../utilities/validators';
import { frequencyTypes, frequencyLabel } from '../../../common/enums/FrequencyTypes';
import { expenseTypes, expenseLabel } from '../../../common/enums/ExpenseTypes';
import { useTranslation } from 'react-i18next';

import type { FrequencyType } from '../../../common/enums/FrequencyTypes';
import type { ExpenseType } from '../../../common/enums/ExpenseTypes';
import type { FieldValidation } from '../../../utilities/validation';
import type { Expense } from '../IncomeCalculator';

type Props = {
  amount?: string,
  expenseFrequency?: FrequencyType,
  expenseType?: ExpenseType,
  onCancelAddExpense: () => void,
  onDoneClick: (expenseRecord: Expense) => void,
};

export const ExpenseFields = (props: Props) => {
  const [amount, setAmount] = useState<string>(props.amount || '');
  const [expenseFrequency, setExpenseFrequency] = useState<string>(props.expenseFrequency || '');
  const [expenseType, setExpenseType] = useState<string>(props.expenseType || '');
  const [formErrors, setErrors] = useState<{ [string]: string }>({});

  const { t } = useTranslation();

  let errorRef = useRef(null);

  const handleUpdateField = (fieldName: string, value: string) => {
    switch (fieldName) {
      case 'expenseType':
        setExpenseType(value);
        break;
      case 'expenseFrequency':
        setExpenseFrequency(value);
        break;
      default:
        throw new Error('Not a valid field name');
    }
    if (formErrors[fieldName]) {
      validate(fieldName);
    }
  };

  const handleUpdateAmount = (event: SyntheticInputEvent<HTMLInputElement>, blur: boolean = false) => {
    const amount = unmaskValue(event.target.value, 'currency');
    setAmount(amount);
    if (blur) {
      validate('amount');
    }
  };

  const getValidations = (): Array<FieldValidation<string>> => {
    const { expenseTypeRequired, frequencyRequired, betweenNegMillionAndPosMillion } = validators;
    const validations = [];
    validations.push(['expenseType', expenseTypeRequired, expenseType]);
    validations.push(['amount', betweenNegMillionAndPosMillion, amount]);
    validations.push(['expenseFrequency', frequencyRequired, expenseFrequency]);
    return validations;
  };

  const validate = async (fieldName?: string) => {
    const { errors } = await runValidations(getValidations(), formErrors, fieldName);
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDoneClick = async () => {
    //$FlowFixMe
    if (errorRef && errorRef.focus) {
      errorRef.focus();
    }

    if (await validate()) {
      // if all fields pass validation
      // calculate the total Expense based on the field state (so this logic only lives here, in one place)
      // then pass entire Expense state up to be stored in parent container
      const multipliers = {
        [frequencyTypes.YEARLY]: 1,
        [frequencyTypes.ONE_TIME_PAYMENT]: 1,
        [frequencyTypes.MONTHLY]: 12,
        [frequencyTypes.WEEKLY]: 51.96, // TODO - verify we want to use this
        '': 0,
      };

      const calculatedTotal = parseFloat(amount) * multipliers[expenseFrequency];
      props.onDoneClick({ amount, expenseType, expenseFrequency, calculatedTotal });
    }
  };

  const handleCancelClick = () => {
    props.onCancelAddExpense();
  };

  return (
    <div className="ds-l-md-col--12">
      <div className="ds-l-row">
        <div className="ds-l-md-col--4">
          <Dropdown
            focustrigger="true"
            name="expense-type"
            label={t('expenseType')}
            options={[
              { label: t(expenseLabel.DEFAULT), value: '' },
              { label: t(expenseLabel.STUDENT_LOAN_INTEREST), value: expenseTypes.STUDENT_LOAN_INTEREST },
              { label: t(expenseLabel.ALIMONY_PAYMENT), value: expenseTypes.ALIMONY_PAYMENT },
              { label: t(expenseLabel.IRA_CONTRIBUTIONS), value: expenseTypes.IRA_CONTRIBUTIONS },
              { label: t(expenseLabel.EDUCATOR_EXPENSES), value: expenseTypes.EDUCATOR_EXPENSES },
            ]}
            onChange={(e) => handleUpdateField('expenseType', e.currentTarget.value)}
            onBlur={() => validate('expenseType')}
            value={expenseType}
            errorMessage={formErrors.expenseType}
          />
        </div>
        <div className="ds-l-md-col--8 ds-u-display--flex ds-u-flex-wrap--wrap">
          <TextField
            className="ds-u-margin-right--4"
            ariaLabel="Enter Expense amount in dollars."
            label={t('amount')}
            mask="currency"
            inputMode="numeric"
            pattern="\d*"
            type="text"
            name="amount"
            size="medium"
            onChange={(e) => handleUpdateAmount(e)}
            onBlur={(e) => handleUpdateAmount(e, true)} // blur will format the currency to two digits so we should update state
            value={amount}
            errorMessage={formErrors.amount}
          />
          <Dropdown
            name="expense-frequency"
            className="ds-l-md-col--4 ds-u-padding--0"
            label={t('howOften')}
            size="medium"
            options={[
              { label: t(frequencyLabel.DEFAULT), value: '' },
              { label: t(frequencyLabel.WEEKLY), value: frequencyTypes.WEEKLY },
              { label: t(frequencyLabel.MONTHLY), value: frequencyTypes.MONTHLY },
              { label: t(frequencyLabel.YEARLY), value: frequencyTypes.YEARLY },
              { label: t(frequencyLabel.ONE_TIME_PAYMENT), value: frequencyTypes.ONE_TIME_PAYMENT },
            ]}
            onChange={(e) => handleUpdateField('expenseFrequency', e.currentTarget.value)}
            onBlur={() => validate('expenseFrequency')}
            value={expenseFrequency}
            errorMessage={formErrors.expenseFrequency}
          />
        </div>
      </div>
      <div className="ds-l-row ds-u-margin-top--3">
        <div className="ds-l-md-col--12">
          <Button
            className="ds-u-margin-right--2 ds-u-margin-bottom--2 ds-c-button--solid"
            onClick={handleDoneClick}
            aria-label={`${t('calculate')} ${t('expenses')}`}
          >
            {t('calculate')}
          </Button>
          <Button onClick={handleCancelClick} aria-label={`${t('cancel')} ${t('expenses')}`}>
            {t('cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExpenseFields;
